import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface NavigationState {
  screen: string;
  params?: any;
}

const findHistoryMatchIndex = (
  history: NavigationState[],
  screen: string,
  productType: any,
  breadCrumbLabel?: string
) => {
  for (let i = history.length - 1; i >= 0; i--) {
    const item = history[i];
    if (
      item.screen === screen &&
      item.params?.productType === productType &&
      (!breadCrumbLabel || item.params?.breadCrumbLabel === breadCrumbLabel)
    ) {
      return i;
    }
  }

  // Same builder crumb can come back on a different screen (e.g. search
  // results → bulk-init → search results). Reuse that entry instead of appending.
  if (breadCrumbLabel) {
    for (let i = history.length - 1; i >= 0; i--) {
      const item = history[i];
      if (
        item.params?.breadCrumbLabel === breadCrumbLabel &&
        item.params?.productType === productType
      ) {
        return i;
      }
    }
  }

  return -1;
};

const initialState: NavigationHistoryState = {
  history: [],
  currentIndex: -1,
  showConfirmationDialog: false,
  targetNavigationIndex: null,
};

const navigationHistorySlice = createSlice({
  name: "navigationHistory",
  initialState,
  reducers: {
    addToHistory: (state, action: PayloadAction<NavigationState>) => {
      const incoming = action.payload;
      const breadCrumbLabel = incoming.params?.breadCrumbLabel;
      if (breadCrumbLabel) {
        const existingIndex = findHistoryMatchIndex(
          state.history,
          incoming.screen,
          incoming.params?.productType,
          breadCrumbLabel
        );
        if (existingIndex !== -1) {
          return;
        }
      }

      // Remove any future history if we're not at the end
      state.history = state.history.slice(0, state.currentIndex + 1);
      state.history.push(incoming);
      state.currentIndex = state.history.length - 1;
    },
    navigateToHistory: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0 && action.payload < state.history.length) {
        if (action.payload < state.currentIndex) {
          state.history = state.history.slice(0, action.payload + 1);
        }
        state.currentIndex = action.payload;
      }
    },
    navigateToPreviousScreen: (state) => {
      state.history = state.history.slice(0, state.currentIndex);
      state.currentIndex = state.history.length - 2;
    },
    clearHistory: (state) => {
      state.history = [];
      state.currentIndex = -1;
      state.showConfirmationDialog = false;
      state.targetNavigationIndex = null;
    },
    showNavigationConfirmation: (state, action: PayloadAction<number>) => {
      state.showConfirmationDialog = true;
      state.targetNavigationIndex = action.payload;
    },
    hideNavigationConfirmation: (state) => {
      state.showConfirmationDialog = false;
      state.targetNavigationIndex = null;
    },

    handleNavigationState: (state, action: PayloadAction<any>) => {
      const screen = action.payload?.screen;
      const params = action.payload?.params;
      const productType = params?.productType;
      const breadCrumbLabel = params?.breadCrumbLabel;

      const matchIndex = findHistoryMatchIndex(
        state.history,
        screen,
        productType,
        breadCrumbLabel
      );

      if (matchIndex !== -1) {
        if (matchIndex < state.currentIndex) {
          state.history = state.history.slice(0, matchIndex + 1);
        }
        state.currentIndex = matchIndex;
      } else if (breadCrumbLabel && state.history.length > 0) {
        state.history = state.history.slice(0, state.currentIndex + 1);
        state.history.push({ screen, params });
        state.currentIndex = state.history.length - 1;
      }

      state.showConfirmationDialog = false;
      state.targetNavigationIndex = null;
    },
    updateProductIdAtIndex: (
      state,
      action: PayloadAction<{ index: number; productID: number }>
    ) => {
      const { index, productID } = action.payload;
      if (index >= 0 && index < state.history.length) {
        state.history[index] = {
          ...state.history[index],
          params: {
            ...state.history[index].params,
            productID,
          },
        };
      }
    },
  },
});

export const {
  addToHistory,
  navigateToHistory,
  clearHistory,
  showNavigationConfirmation,
  hideNavigationConfirmation,
  navigateToPreviousScreen,
  handleNavigationState,
  updateProductIdAtIndex,
} = navigationHistorySlice.actions;

export default navigationHistorySlice.reducer;
